import React, { useState } from 'react';
import { useClientContext } from 'contexts/client';
import { 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TableSortLabel,
  Paper,
  Switch,
  FormControlLabel,
  Typography,
  Box
} from '@mui/material';

const CardStyle = {
  margin: '10px',
  padding: '15px',
  border: '1px solid #ccc',
  borderRadius: '4px',
  backgroundColor: '#f9f9f9',
  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  overflow: 'auto'
};

const HeaderStyle = {
  borderBottom: '1px solid #eee',
  paddingBottom: '10px',
  marginBottom: '10px',
  fontWeight: 'bold',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
};

// Helper function to determine if data is tabular (array of objects with consistent keys)
const isTabularData = (data) => {
  if (!Array.isArray(data) || data.length === 0) return false;
  if (typeof data[0] !== 'object' || data[0] === null) return false;
  
  // Check if all items have the same keys
  const keys = Object.keys(data[0]);
  return data.every(item => 
    typeof item === 'object' && 
    item !== null &&
    Object.keys(item).length > 0 && 
    keys.every(key => key in item)
  );
};

// Helper function to get all unique keys from array of objects
const getAllKeys = (data) => {
  if (!Array.isArray(data) || data.length === 0) return [];
  
  const keySet = new Set();
  data.forEach(item => {
    if (typeof item === 'object' && item !== null) {
      Object.keys(item).forEach(key => keySet.add(key));
    }
  });
  
  return Array.from(keySet);
};

// Function to get the value for sorting
const getSortValue = (obj, key) => {
  const value = obj[key];
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value.toLowerCase();
  if (typeof value === 'object') return JSON.stringify(value).toLowerCase();
  return value;
};

// Sort function for data
const sortData = (data, key, direction) => {
  return [...data].sort((a, b) => {
    const valueA = getSortValue(a, key);
    const valueB = getSortValue(b, key);
    
    if (valueA < valueB) return direction === 'asc' ? -1 : 1;
    if (valueA > valueB) return direction === 'asc' ? 1 : -1;
    return 0;
  });
};

// Convert data to CSV string
const convertToCSV = (data, keys) => {
  if (!Array.isArray(data) || data.length === 0) return '';
  
  // Use all keys if not provided
  const headers = keys || getAllKeys(data);
  
  // Create header row
  let csv = headers.join(',') + '\n';
  
  // Add data rows
  data.forEach(item => {
    const row = headers.map(header => {
      const value = item[header];
      // Handle different types of values
      if (value === null || value === undefined) return '';
      if (typeof value === 'object') return '"' + JSON.stringify(value).replace(/"/g, '""') + '"';
      return '"' + String(value).replace(/"/g, '""') + '"';
    });
    csv += row.join(',') + '\n';
  });
  
  return csv;
};

// Download data as CSV file
const downloadCSV = (data, filename) => {
  if (!Array.isArray(data) || data.length === 0) return;
  
  const keys = getAllKeys(data);
  const csv = convertToCSV(data, keys);
  
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const TableView = ({ data }) => {
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  if (!isTabularData(data)) return <div>Data is not tabular</div>;
  
  const keys = getAllKeys(data);
  
  // Sort data if sort config is set
  const sortedData = sortConfig.key 
    ? sortData(data, sortConfig.key, sortConfig.direction) 
    : data;
  
  const handleSort = (key) => {
    const direction = 
      sortConfig.key === key && sortConfig.direction === 'asc' 
        ? 'desc' 
        : 'asc';
    
    setSortConfig({ key, direction });
  };
  
  return (
    <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
      <Table stickyHeader aria-label="data table" size="small">
        <TableHead>
          <TableRow>
            {keys.map(key => (
              <TableCell key={key}>
                <TableSortLabel
                  active={sortConfig.key === key}
                  direction={sortConfig.key === key ? sortConfig.direction : 'asc'}
                  onClick={() => handleSort(key)}
                >
                  {key}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {keys.map(key => (
                <TableCell key={`${rowIndex}-${key}`}>
                  {typeof row[key] === 'object' && row[key] !== null 
                    ? JSON.stringify(row[key])
                    : String(row[key] ?? '')}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const OptionsCard = ({ title, data }) => {
  const [viewAsTable, setViewAsTable] = useState(true);
  const canViewAsTable = isTabularData(data);
  
  return (
    <div style={CardStyle}>
      <div style={HeaderStyle}>
        <Typography variant="h6">{title}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canViewAsTable && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={viewAsTable}
                    onChange={(e) => setViewAsTable(e.target.checked)}
                    name="viewAsTable"
                    color="primary"
                  />
                }
                label="Table View"
              />
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => downloadCSV(data, `${title.replace(/\s+/g, '_').toLowerCase()}.csv`)}
              >
                Export CSV
              </Button>
            </>
          )}
        </Box>
      </div>
      
      {canViewAsTable && viewAsTable ? (
        <TableView data={data} />
      ) : (
        <pre style={{ maxHeight: '300px', overflow: 'auto' }}>
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
};

const ReportingOptionsDebug = () => {
  // This will work with both branch versions of useClientContext
  const clientContext = useClientContext();
  
  // Handle both the tuple format from main and the object format from ninja
  const state = Array.isArray(clientContext) ? clientContext[0] : clientContext.state;
  
  // Extract reporting options
  const { reportingOptions = {} } = state?.client || {};
  
  return (
    <div style={{ padding: '20px' }}>
      <Typography variant="h4" gutterBottom>Reporting Options Debug Panel</Typography>
      <Typography paragraph>
        This panel shows all currently loaded reporting options from the client context.
        For tabular data, you can toggle between Table/JSON views, sort columns by clicking on headers, and export as CSV.
      </Typography>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <OptionsCard 
          title="Store Attribute Options" 
          data={reportingOptions.storeAttrOptions || []} 
        />
        
        <OptionsCard 
          title="Store Group By Options" 
          data={reportingOptions.storeGroupByOptions || []} 
        />
        
        <OptionsCard 
          title="Item Attribute Options" 
          data={reportingOptions.itemAttrOptions || []} 
        />
        
        <OptionsCard 
          title="Store Filter Options" 
          data={reportingOptions.storeFilterOptions || []} 
        />
        
        <OptionsCard 
          title="Item Group Column Options" 
          data={reportingOptions.itemGroupColumnOptions || []} 
        />
        
        <OptionsCard 
          title="Runtime Item Group Filter Options" 
          data={reportingOptions.runtimeItemGroupFilterOptions || []} 
        />
      </div>
      
      {/* Show data thru dates if they exist (only in ninja) */}
      {state?.client?.dataThruByWarehouse && (
        <OptionsCard 
          title="Data Thru By Warehouse" 
          data={state.client.dataThruByWarehouse} 
        />
      )}
      
      <div style={{ marginTop: '20px' }}>
        <Typography variant="h5" gutterBottom>Full Client Context</Typography>
        <pre style={{ 
          maxHeight: '500px', 
          overflow: 'auto', 
          backgroundColor: '#f0f0f0', 
          padding: '10px',
          border: '1px solid #ddd',
          borderRadius: '4px'
        }}>
          {JSON.stringify(state, null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default ReportingOptionsDebug;